import { CardContainer } from "@msys/ui";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, Button, LinearProgress, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";

interface TierStat {
  label: string;
  value: string;
  color?: string;
}

interface Props {
  title: string;
  count: number;
  percentage: number;
  ringColor: string;
  stats: TierStat[];
  onTrigger?: () => void;
  onCancel?: () => void;
  triggerLabel?: string;
  cancelLabel?: string;
  triggerDisabled?: boolean;
  isRunning?: boolean;
  pending?: boolean;
  warning?: string;
  progress?: number;
}

export const TierCard = ({
  title,
  count,
  percentage,
  ringColor,
  stats,
  onTrigger,
  onCancel,
  triggerLabel = "Trigger",
  cancelLabel = "Cancel",
  triggerDisabled,
  isRunning,
  pending,
  warning,
  progress,
}: Props) => {
  const { t } = useTranslate(["Integrations"]);
  const circumference = 2 * Math.PI * 42;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <CardContainer
      title={title}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        "& > .MuiCollapse-root": {
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
        },
        "& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner": {
          height: "100%",
        },
      }}
    >
      <Stack spacing={1.5} sx={{ p: 1, height: "100%" }}>
        {/* Info banner */}
        {warning && (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              {warning}
            </Typography>
          </Stack>
        )}

        {/* Progress bar */}
        {isRunning && (
          <LinearProgress
            variant={
              progress !== undefined && progress > 0
                ? "determinate"
                : "indeterminate"
            }
            value={progress !== undefined ? progress * 100 : undefined}
            sx={{ "& .MuiLinearProgress-bar": { backgroundColor: ringColor } }}
          />
        )}

        <Stack
          direction="row"
          spacing={2.5}
          sx={{ alignItems: "center", flexGrow: 1 }}
        >
          {/* Donut chart */}
          <Box
            sx={{
              position: "relative",
              width: 100,
              height: 100,
              flexShrink: 0,
            }}
          >
            <svg viewBox="0 0 100 100" width="100%" height="100%">
              <circle
                cx="50"
                cy="50"
                r="42"
                fill="none"
                stroke="#e0e0e0"
                strokeWidth="6"
              />
              <circle
                cx="50"
                cy="50"
                r="42"
                fill="none"
                stroke={ringColor}
                strokeWidth="6"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                transform="rotate(-90 50 50)"
              />
            </svg>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {count.toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {percentage.toFixed(1)}%
              </Typography>
            </Box>
          </Box>

          {/* Stats table */}
          <Stack spacing={0} sx={{ flex: 1 }}>
            {stats.map((stat, i) => (
              <Stack
                key={i}
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  py: 0.75,
                  borderBottom: i < stats.length - 1 ? "1px solid" : "none",
                  borderColor: "divider",
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {stat.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: stat.color ?? "text.primary" }}
                >
                  {stat.value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>

        {/* Trigger/Cancel button */}
        {(onTrigger || onCancel) && (
          <Button
            size="small"
            variant="outlined"
            color={isRunning ? "error" : "primary"}
            disabled={triggerDisabled || pending}
            onClick={isRunning ? onCancel : onTrigger}
            sx={{ alignSelf: "flex-end" }}
          >
            {isRunning
              ? cancelLabel
              : pending
                ? t("Requested...", { ns: "Integrations" })
                : triggerLabel}
          </Button>
        )}
      </Stack>
    </CardContainer>
  );
};
